import { AgentUserPagingResponse } from '@/__generated';
import AppFormSelect from '@/components/App/AppFormSelect';
import FormItem from '@/components/Form/FormItem';
import Spin from '@/components/Spin';
import { UNKNOWN_ERROR_MESSAGE } from '@/config';
import { AgentRoleType } from '@/constants';
import { ModalUtils } from '@/contexts/ModalWrapper';
import { FormValidation } from '@/helpers/formValidations';
import { useGetAllCountries } from '@/hooks/dataProvider';
import { apiSdk } from '@/httpClient';
import { ErrorMessage } from '@hookform/error-message';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Modal, ModalBody } from 'reactstrap';

type Props = {
  onClose: (reload: boolean) => void;
  user: AgentUserPagingResponse | null | undefined;
};

export default function CreateUpdateAgentModal({ onClose, user }: Props) {
  const [loading, setLoading] = React.useState<boolean>(false);
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<any>({
    defaultValues: user ? { ...user, active: user?.active ? 'Yes' : 'No' } : { title: 'Mr', active: 'Yes' },
  });
  const { data: countries } = useGetAllCountries();

  const phoneCodeOptions = React.useMemo(() => {
    return countries?.data?.map(v => ({ value: v.phoneCode, label: v.phoneCode }));
  }, [countries]);
  const onSubmit = values => {
    setLoading(true);
    values.active = values.active !== 'No';
    values.phoneCode = values.phoneCode?.value;
    let api = user ? apiSdk.AgentUserService.updateUser({ body: { ...values, id: user.id } }) : apiSdk.AgentUserService.createUser({ body: values });
    api
      .then(res => {
        if (res.success) {
          onClose(true);
        } else {
          ModalUtils.error({
            content: t(res.message || UNKNOWN_ERROR_MESSAGE),
          });
        }
      })
      .catch(() => {
        ModalUtils.error({
          content: t(UNKNOWN_ERROR_MESSAGE),
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };
  return (
    <Modal isOpen={true} unmountOnClose={true}>
      <ModalBody>
        <Spin loading={loading}>
          <div className="text-18 fw-500 mb-10">{user ? t('agentUserPage_UpdateUser') : t('agentUserPage_CreateUser')}</div>
          <form className="row y-gap-10 x-gap-5" onSubmit={handleSubmit(onSubmit)}>
            <div className="col-12">
              <div className="form-item-radio">
                <label className={`lh-1 text-14 text-light-1 label required`}>{t('base.Roles')}</label>
                <div className="d-flex radio-container">
                  {AgentRoleType.__getRoles.map(r => {
                    return (
                      <div className="d-flex items-center radio-item" key={r}>
                        <input {...register('roles', FormValidation.Required)} type="checkbox" value={r} id={`roles-${r}`} />
                        <label htmlFor={`roles-${r}`}>{r}</label>
                      </div>
                    );
                  })}
                </div>
              </div>
              <ErrorMessage errors={errors} name={'roles'} as="span" className="form-item-error" />
            </div>
            <div className="col-12">
              <FormItem isRequired errors={errors} label={t('base.FirstName')} name="firstName">
                <input {...register('firstName', FormValidation.Required)} />
              </FormItem>
            </div>

            <div className="col-12">
              <FormItem isRequired errors={errors} label={t('base.LastName')} name="lastName">
                <input {...register('lastName', FormValidation.Required)} />
              </FormItem>
            </div>
            <div className="col-12">
              <FormItem isRequired errors={errors} label={t('base.Email')} name="email">
                <input {...register('email', { ...FormValidation.Required, ...FormValidation.Email })} />
              </FormItem>
            </div>
            <div className={`col-12`}>
              <FormItem isRequired errors={errors} label={t('base.UserName')} name="userName">
                <input type="text" {...register('userName', FormValidation.Required)} disabled={!!user} />
              </FormItem>
            </div>
            <div className={`col-6`}>
              <div className="form-item-select">
                <div className={`lh-1 text-14 text-light-1 label`}>Phone Code</div>
                <div className="select-container">
                  <Controller
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <AppFormSelect
                        classNamePrefix="omega-select"
                        className="w-100"
                        options={phoneCodeOptions}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        placeholder="Phone Code"
                        isClearable
                      />
                    )}
                    name="phoneCode"
                  />
                </div>
              </div>
              <ErrorMessage errors={errors} name={'phoneCode'} as="span" className="form-item-error" />
            </div>
            <div className="col-6">
              <FormItem errors={errors} label={t('base.PhoneNumber')} name="phoneNumber">
                <input type="text" {...register('phoneNumber', FormValidation.PhoneNumber)} />
              </FormItem>
            </div>
            {user ? (
              <div className="col-6">
                <div className="form-item-radio">
                  <label className={`lh-1 text-14 text-light-1 label required`}>{t('base.Active')}</label>
                  <div className="d-flex radio-container">
                    <div className="d-flex items-center radio-item">
                      <input {...register('active')} type="radio" value="Yes" id="active_Yes" />
                      <label htmlFor="active_Yes">{t('base.Yes')}</label>
                    </div>
                    <div className="d-flex items-center radio-item">
                      <input {...register('active')} type="radio" value="No" id="active_No" />
                      <label htmlFor="active_No">{t('base.No')}</label>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="col-12 d-flex justify-end">
              <button
                type="button"
                className="button px-15 py-10 -outline-light-1 mr-10"
                onClick={() => {
                  onClose(false);
                }}
              >
                <i className="icon-close text-12 px-5" />
                {t('base.Close')}
              </button>
              <button type="submit" className="button px-15 bg-blue-1  py-10 text-white">
                <i className="icon-check text-12 px-5" />
                {t('base.Save')}
              </button>
            </div>
          </form>
        </Spin>
      </ModalBody>
    </Modal>
  );
}
